import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  TablePagination,
  IconButton,
  Tooltip,
} from '@mui/material'
import { Close, Subscriptions, Download, OpenInNew } from '@mui/icons-material'
import { getTierLabel } from '../utils/tierUtils'

interface SubscriptionItem {
  org_uuid: string
  org_name: string
  plan_name: string
  payment_type: string
  billing_frequency: string
  tier: number
  billing_amount: number | null
  monthly_amount: number
  annual_amount: number
}

interface SubscriptionBreakdownData {
  subscriptions: SubscriptionItem[]
  total_count: number
  total_arr: number
  total_mrr: number
}

interface SubscriptionBreakdownModalProps {
  open: boolean
  onClose: () => void
  data: SubscriptionBreakdownData | null
  loading: boolean
  error?: string
}

const SubscriptionBreakdownModal: React.FC<SubscriptionBreakdownModalProps> = ({
  open,
  onClose,
  data,
  loading,
  error,
}) => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  const formatCurrency = (amount: number | undefined | null) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return '$0'
    }
    return `$${amount.toLocaleString()}`
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const getTierColor = (tier: number) => {
    switch (tier) {
      case 0:
        return 'default'
      case 1:
        return 'error'
      case 2:
        return 'warning'
      case 3:
        return 'info'
      case 4:
        return 'success'
      default:
        return 'default'
    }
  }

  const getPaymentTypeColor = (paymentType: string) => {
    switch (paymentType.toLowerCase()) {
      case 'stripe':
        return 'primary'
      case 'invoice':
        return 'secondary'
      default:
        return 'default'
    }
  }

  const createOrgLink = (orgName: string, orgUuid: string) => {
    if (!orgUuid) {
      return (
        <Typography variant="body2" fontWeight="medium">
          {orgName}
        </Typography>
      )
    }

    return (
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography
          component="a"
          href={`/orgs/${orgUuid}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            textDecoration: 'none',
            fontWeight: 'medium',
            color: 'primary.main',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {orgName}
        </Typography>
        <IconButton
          size="small"
          component="a"
          href={`/orgs/${orgUuid}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ p: 0.25 }}
        >
          <OpenInNew fontSize="small" />
        </IconButton>
      </Box>
    )
  }

  const handleDownloadCSV = () => {
    if (!data?.subscriptions || data.subscriptions.length === 0) {
      return
    }

    const headers = [
      'Organisation Name',
      'Plan Name',
      'Payment Type',
      'Billing Frequency',
      'Tier',
      'Billing Amount',
      'Monthly Amount',
      'Annual Amount',
      'Org UUID',
    ]

    const csvContent = [
      headers.join(','),
      ...data.subscriptions.map((sub) =>
        [
          `"${sub.org_name.replace(/"/g, '""')}"`,
          `"${sub.plan_name.replace(/"/g, '""')}"`,
          sub.payment_type,
          sub.billing_frequency,
          sub.tier,
          sub.billing_amount || 0,
          sub.monthly_amount,
          sub.annual_amount,
          sub.org_uuid,
        ].join(',')
      ),
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute(
        'download',
        `subscription-breakdown-${new Date().toISOString().split('T')[0]}.csv`
      )
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const paginatedSubscriptions =
    data?.subscriptions?.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    ) || []

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Subscriptions color="primary" />
            <Typography variant="h6">Subscription Breakdown</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            {data && (
              <>
                <Chip
                  label={`${data.total_count} subscriptions`}
                  color="info"
                  variant="outlined"
                  size="small"
                />
                <Chip
                  label={`Total ARR: ${formatCurrency(data.total_arr)}`}
                  color="primary"
                  variant="outlined"
                />
                <Tooltip title="Download CSV">
                  <IconButton
                    onClick={handleDownloadCSV}
                    disabled={
                      !data?.subscriptions || data.subscriptions.length === 0
                    }
                    color="primary"
                  >
                    <Download />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Button onClick={onClose} size="small" color="inherit">
              <Close />
            </Button>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={4}
          >
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading subscription details...
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : !data || data.subscriptions.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="textSecondary">
              No subscription data available.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Summary Cards */}
            <Box display="flex" gap={2} mb={3}>
              <Paper sx={{ p: 2, flex: 1, bgcolor: 'primary.50' }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Total ARR
                </Typography>
                <Typography variant="h5" color="primary">
                  {formatCurrency(data.total_arr)}
                </Typography>
              </Paper>

              <Paper sx={{ p: 2, flex: 1, bgcolor: 'info.50' }}>
                <Typography variant="subtitle2" color="info.main" gutterBottom>
                  Monthly Revenue (MRR)
                </Typography>
                <Typography variant="h5" color="info.main">
                  {formatCurrency(data.total_mrr)}
                </Typography>
              </Paper>

              <Paper sx={{ p: 2, flex: 1, bgcolor: 'success.50' }}>
                <Typography
                  variant="subtitle2"
                  color="success.main"
                  gutterBottom
                >
                  Active Subscriptions
                </Typography>
                <Typography variant="h5" color="success.main">
                  {data.total_count}
                </Typography>
              </Paper>
            </Box>

            {/* Subscriptions Table */}
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ border: 1, borderColor: 'divider' }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Organisation</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Plan</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Payment</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Frequency</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Tier</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Monthly</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Annual</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedSubscriptions.map((subscription, index) => (
                    <TableRow key={`${subscription.org_uuid}-${index}`} hover>
                      <TableCell>
                        {createOrgLink(
                          subscription.org_name,
                          subscription.org_uuid
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {subscription.plan_name}
                        </Typography>
                        {subscription.billing_amount && (
                          <Typography variant="caption" color="textSecondary">
                            Custom:{' '}
                            {formatCurrency(subscription.billing_amount)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={subscription.payment_type}
                          size="small"
                          variant="outlined"
                          color={
                            getPaymentTypeColor(
                              subscription.payment_type
                            ) as any
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {subscription.billing_frequency}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getTierLabel(subscription.tier)}
                          size="small"
                          color={getTierColor(subscription.tier) as any}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          color="info.main"
                        >
                          {formatCurrency(subscription.monthly_amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          color="primary"
                        >
                          {formatCurrency(subscription.annual_amount)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={data.total_count}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ mt: 2 }}
            />

            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="textSecondary">
                <strong>Note:</strong> This breakdown shows all active
                subscriptions contributing to the Annual Recurring Revenue
                (ARR). Monthly amounts are calculated based on billing
                frequency. Custom billing amounts override standard tier
                pricing.
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SubscriptionBreakdownModal
